import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import BugReportLink from '../components/BugReportLink';
import ArabicLoader from '../components/ArabicLoader';
import FilterBar from '../components/FilterBar';
import SpotlightTour from '../components/SpotlightTour';
import { THEME_META, THEME_TOPICS } from '../data/themes';
import { THEME_ORDER } from '../data/path-poems';
import { readUserLevel, readLearningTrack, getRecommendedThemes, getRecommendedThemesForUser, pickSurpriseTheme } from '../lib/theme-recommendations';
import { lsGet, lsSet } from '../lib/storage';
import { normalizeRoman, arabicToLatin } from '../lib/arabic-utils';
import { supabase } from '../lib/supabase';
import { TierDisplay } from '../lib/tiers.jsx';
import { prefetchPoem } from '../lib/prefetch';

const API = window.location.origin;

// ── Build PATHS from canonical data (pct + live count injected at runtime) ────

const PATHS = THEME_ORDER.map(id => ({
  id,
  ...THEME_META[id],
  poemCount: 0,   // replaced by themeCounts from API once loaded
}));

// ── Filter configuration ──────────────────────────────────────────────────────

const FILTER_TYPES   = ['Theme', 'Era', 'Difficulty'];
const THEME_OPTIONS  = ['All', 'Wisdom', 'Love', 'Sufi', 'Heroism', 'Elegy', 'Asceticism', 'Nature', 'Praise', 'Generosity', 'Satire', 'Muʿallaqāt', 'Longing'];
const ERA_OPTIONS    = ['All', 'Pre-Islamic', 'Umayyad', 'Abbasid', 'Medieval', 'Andalusian'];
const DIFF_OPTIONS   = ['All', 'A2', 'B1', 'B2', 'C1'];

const FILTER_OPTIONS = { Theme: THEME_OPTIONS, Era: ERA_OPTIONS, Difficulty: DIFF_OPTIONS };

const THEME_ID_MAP = {
  Wisdom: 'hikma', Love: 'ghazal', Sufi: 'sufi', Heroism: 'fakhr',
  Elegy: 'ritha', Asceticism: 'zuhd', Nature: 'wasf', Praise: 'madih',
  Generosity: 'karam', Satire: 'hija', 'Muʿallaqāt': 'muallaqat', Longing: 'shawq',
};

// Static era membership per theme — replaces the old THEME_POEMS-derived lookup
const THEME_ERAS = {
  hikma:     ['Pre-Islamic', 'Abbasid'],
  ghazal:    ['Umayyad', 'Andalusian'],
  sufi:      ['Abbasid', 'Medieval'],
  fakhr:     ['Pre-Islamic', 'Umayyad', 'Abbasid'],
  ritha:     ['Pre-Islamic', 'Abbasid'],
  zuhd:      ['Abbasid'],
  wasf:      ['Abbasid', 'Andalusian'],
  madih:     ['Pre-Islamic', 'Umayyad', 'Abbasid'],
  karam:     ['Pre-Islamic', 'Umayyad', 'Abbasid'],
  hija:      ['Umayyad'],
  muallaqat: ['Pre-Islamic'],
  shawq:     ['Pre-Islamic', 'Umayyad', 'Abbasid'],
};

// readUserLevel is imported from themeRecommendations

// ── PoetSearch ────────────────────────────────────────────────────────────────

function PoetSearch({ poets, selected, onChange }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [poemResults, setPoemResults] = useState([]);

  const poetResults = search.trim().length >= 1
    ? poets.filter(p => {
        const q = search.trim();
        const qLower = q.toLowerCase();
        const isLatin = /[a-z]/i.test(q);
        return (
          p.nameAr.includes(q) ||
          (p.nameEn || '').toLowerCase().includes(qLower) ||
          (isLatin && normalizeRoman(p.nameEn || '').includes(normalizeRoman(qLower))) ||
          (isLatin && arabicToLatin(p.nameAr).includes(normalizeRoman(qLower)))
        );
      }).slice(0, 20)
    : [];

  // Debounced poem search
  useEffect(() => {
    const q = search.trim();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (q.length < 2) { setPoemResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/api/poems/browse?q=${encodeURIComponent(q)}&limit=6`)
        .then(r => r.json())
        .then(d => setPoemResults(d.poems || []))
        .catch(() => setPoemResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Clear poem results when search is cleared
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (!search.trim()) setPoemResults([]); }, [search]);

  function toggle(poet) {
    const already = selected.some(s => s.nameAr === poet.nameAr);
    onChange(already ? selected.filter(s => s.nameAr !== poet.nameAr) : [...selected, poet]);
  }

  const hasSelection = selected.length > 0;
  const hasResults = poetResults.length > 0 || poemResults.length > 0;

  return (
    <div style={{ marginBottom: '1rem' }}>

      {/* Search input */}
      <div style={{ position: 'relative', marginBottom: hasResults ? '0.5rem' : 0 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search poets or poem opening lines…"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '0.45rem 0.75rem',
            color: 'var(--text)', fontSize: '0.82rem', outline: 'none',
            fontFamily: "'Noto Naskh Arabic', serif",
          }}
        />
      </div>

      {/* Poet chips — only shown while typing */}
      {poetResults.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
          {poetResults.map(p => {
            const isSelected = selected.some(s => s.nameAr === p.nameAr);
            return (
              <button
                key={p.nameAr}
                onClick={() => toggle(p)}
                style={{
                  background: isSelected ? 'var(--gold-subtle)' : 'var(--bg2)',
                  border: `1px solid ${isSelected ? 'var(--gold-line)' : 'var(--border)'}`,
                  borderRadius: 20, padding: '0.28rem 0.75rem',
                  cursor: 'pointer', color: isSelected ? 'var(--gold)' : 'var(--text2)',
                  fontFamily: "'Noto Naskh Arabic', serif",
                  fontSize: '0.82rem', direction: 'rtl',
                  transition: 'background 0.12s, color 0.12s, border-color 0.12s',
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
              >
                {p.nameAr}
                {p.nameEn && (
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.06em', opacity: 0.55, direction: 'ltr', textTransform: 'uppercase' }}>
                    {p.nameEn}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Poem results — tap to open poem directly */}
      {poemResults.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: '0.5rem' }}>
          {poemResults.map((p, i) => (
            <button
              key={p.id}
              onClick={() => navigate(`/poem/${p.id}`)}
              style={{ width: '100%', background: 'none', border: 'none', borderBottom: i < poemResults.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', padding: '0.55rem 0.75rem', textAlign: 'right', display: 'block' }}
            >
              <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', color: 'var(--text)', direction: 'rtl', lineHeight: 1.4 }}>{p.titleAr}</div>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', marginTop: '0.15rem', textAlign: 'right' }}>{p.poetEn || p.poetAr}</div>
            </button>
          ))}
        </div>
      )}

      {/* Selected poet pills — always visible once chosen */}
      {hasSelection && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
          {selected.map(p => (
            <button
              key={p.nameAr}
              onClick={() => toggle(p)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                background: 'var(--gold-subtle)', border: '1px solid var(--gold-line)',
                borderRadius: 20, padding: '0.28rem 0.75rem',
                cursor: 'pointer', color: 'var(--gold)',
                fontFamily: "'Noto Naskh Arabic', serif",
                fontSize: '0.82rem', direction: 'rtl',
              }}
            >
              {p.nameAr}
              <span style={{ fontFamily: 'sans-serif', fontSize: '0.55rem', opacity: 0.7 }}>✕</span>
            </button>
          ))}
          <button
            onClick={() => onChange([])}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem 0.1rem',
              fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem',
              letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)',
            }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

// ── PoemCard ──────────────────────────────────────────────────────────────────

function PoemCard({ poem, onClick }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => poem?.id && prefetchPoem(poem.id)}
      onFocus={() => poem?.id && prefetchPoem(poem.id)}
      style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: '0.9rem 1.1rem', marginBottom: '0.55rem',
        cursor: 'pointer', transition: 'border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem' }}>
        <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1rem', color: 'var(--text)', direction: 'rtl', flex: 1, lineHeight: 1.5 }}>
          {poem.titleAr}
        </div>
        <span className="tag" style={{ flexShrink: 0 }}>{poem.level}</span>
      </div>
      {poem.titleEn && (
        <div style={{ fontSize: '0.76rem', color: 'var(--text3)', fontStyle: 'italic', marginBottom: '0.3rem' }}>
          {poem.titleEn}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.74rem', color: 'var(--gold2)' }}>{poem.poetEn || poem.poetAr}</span>
        {poem.era && <span style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>· {poem.era}</span>}
        <span style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>· {poem.bayts} verses</span>
      </div>
      {poem.excerpt && (
        <div style={{
          fontFamily: "'Noto Naskh Arabic', serif",
          fontSize: '0.83rem', color: 'var(--text3)', marginTop: '0.5rem',
          direction: 'rtl', lineHeight: 1.8, opacity: 0.75,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {poem.excerpt}
        </div>
      )}
    </div>
  );
}

// ── PathCard ──────────────────────────────────────────────────────────────────

function PathCard({ path, onPress, pinned, onPin }) {
  return (
    <div
      className="path-card"
      onClick={onPress}
      style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: '1.1rem 1.25rem' }}
    >
      {/* Row 1: icon + names + level badge + pin */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.85rem', marginBottom: '0.5rem' }}>
        <div className="path-icon" style={{ fontSize: '1.15rem', flexShrink: 0 }}>{path.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem', flexWrap: 'wrap' }}>
            <span className="path-name" style={{ lineHeight: 1.3 }}>{path.nameEn}</span>
            <span style={{
              fontFamily: "'Noto Naskh Arabic', serif",
              fontSize: '0.88rem', color: 'var(--gold2)',
            }}>{path.nameAr}</span>
          </div>
        </div>
        <span className="tag tag-gold" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>{path.level}</span>
        {onPin && (
          <button
            onClick={e => { e.stopPropagation(); onPin(path.id); }}
            aria-label={pinned ? 'Unpin theme' : 'Pin theme'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.1rem 0.2rem', lineHeight: 1, flexShrink: 0,
              fontSize: '0.95rem', opacity: pinned ? 1 : 0.3,
              transition: 'opacity 0.15s',
            }}
          >
            ★
          </button>
        )}
      </div>

      {/* Row 2: description */}
      <div style={{
        fontSize: '0.78rem', color: 'var(--text3)', lineHeight: 1.65,
        fontStyle: 'italic', marginBottom: '0.65rem',
      }}>
        {path.desc}
      </div>

      {/* Row 3: tier ring + counter + near-miss */}
      <TierDisplay readCount={path.readCount ?? 0} pathKey={path.id} />
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function AllPaths() {
  const navigate = useNavigate();
  const userId = lsGet('diwanPrismaUserId');
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const filterAreaRef = useRef(null);
  const cardsRef      = useRef(null);
  const discoverRef   = useRef(null);
  const [filterType,  setFilterType]  = useState('Difficulty');
  const [filterValue, setFilterValue] = useState('All');
  const [navError,    setNavError]    = useState(false);
  const [themeCounts,  setThemeCounts]  = useState({});
  const [userPct,      setUserPct]      = useState({});
  const [userStudied,  setUserStudied]  = useState({});
  const [userReadCount, setUserReadCount] = useState({});

  // Pinned themes
  const [pinned, setPinned] = useState(() => {
    try { return JSON.parse(lsGet('diwanPinnedThemes') || '[]'); } catch { return []; }
  });

  function togglePin(id) {
    setPinned(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      lsSet('diwanPinnedThemes', JSON.stringify(next));
      return next;
    });
  }

  // Poet filter state
  const [poets,           setPoets]           = useState([]);
  const [selectedPoets,   setSelectedPoets]   = useState([]);
  const [poetPoems,       setPoetPoems]       = useState([]);
  const [poetPoemsTotal,  setPoetPoemsTotal]  = useState(0);
  const [poetPoemsLoading, setPoetPoemsLoading] = useState(false);

  // Single init fetch — poets + all theme counts in one round-trip (was 11 requests)
  useEffect(() => {
    fetch(`${API}/api/poems/browse?mode=init`)
      .then(r => r.json())
      .then(data => {
        setPoets(data.poets ?? []);
        const counts = {};
        Object.entries(data.themeCounts ?? {}).forEach(([id, total]) => { counts[id] = total; });
        setThemeCounts(counts);
      })
      .catch(err => console.warn('[diwan] browse/init failed', err));
  }, []);

  // Fetch real user progress and compute per-theme completion percentages
  useEffect(() => {
    if (!userId) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      const token   = session?.access_token ?? null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API}/api/users/${userId}/progress`, { headers })
        .then(r => r.json())
        .then(({ progress = [] }) => {
          // Map topic → themeId for quick lookup
          const topicToTheme = {};
          Object.entries(THEME_TOPICS).forEach(([themeId, topics]) => {
            topics.forEach(t => { topicToTheme[t] = themeId; });
          });
          // Fractional bayt-based progress: each poem contributes 0–1 based on bayts read
          const studied      = {};  // whole poems completed (for label)
          const progressSum  = {};  // fractional sum per theme (for bar)
          const readCount    = {};  // poems started or completed (for tier system)
          progress.forEach(({ status, currentVerse, poem }) => {
            const themeId    = topicToTheme[poem?.topic];
            if (!themeId) return;
            const verseCount = poem?.verseCount || 0;
            let fraction;
            if (status === 'STUDIED') {
              fraction = 1.0;
              studied[themeId]   = (studied[themeId]   ?? 0) + 1;
              readCount[themeId] = (readCount[themeId] ?? 0) + 1;
            } else if (status === 'READING' && verseCount > 0) {
              fraction = Math.min(1, (currentVerse + 1) / verseCount);
              readCount[themeId] = (readCount[themeId] ?? 0) + 1;
            } else {
              fraction = 0;
            }
            if (fraction > 0) progressSum[themeId] = (progressSum[themeId] ?? 0) + fraction;
          });
          setUserPct(prev => {
            const next = { ...prev };
            THEME_ORDER.forEach(id => {
              if (progressSum[id] != null) next[id] = progressSum[id];
            });
            return next;
          });
          setUserStudied(studied);
          setUserReadCount(readCount);
        })
        .catch(() => {});
    });
  }, [userId]);

  // Fetch poems whenever selected poets change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (selectedPoets.length === 0) { setPoetPoems([]); return; }
    setPoetPoemsLoading(true);
    const names = selectedPoets.map(p => encodeURIComponent(p.nameAr)).join(',');
    fetch(`${API}/api/poems/browse?poets=${names}&limit=100`)
      .then(r => r.json())
      .then(data => { setPoetPoems(data.poems ?? []); setPoetPoemsTotal(data.total ?? 0); })
      .catch(err => { setPoetPoems([]); setPoetPoemsTotal(0); console.warn('[diwan] poet poems failed', err); })
      .finally(() => setPoetPoemsLoading(false));
  }, [selectedPoets]);

  const isFiltered    = filterValue !== 'All';
  const poetFiltered  = selectedPoets.length > 0;
  const userLevel     = useMemo(() => readUserLevel(), []);
  const learningTrack = useMemo(() => readLearningTrack(), []);

  // Merge live progress percentages into PATHS; recompute when either changes
  const pathsWithPct = useMemo(() =>
    PATHS.map(p => {
      const engagedCount = userPct[p.id];
      const studiedCount = userStudied[p.id] ?? 0;
      const readCount    = userReadCount[p.id] ?? 0;
      const total        = themeCounts[p.id];
      const pct          = engagedCount == null
        ? null
        : total > 0 ? Math.min(100, Math.round((engagedCount / total) * 100)) : 0;
      const studiedPct   = total > 0 ? Math.min(100, Math.round((studiedCount / total) * 100)) : 0;
      return { ...p, pct, studiedPct, readCount };
    }),
    [userPct, userStudied, userReadCount, themeCounts]
  );

  // Top 3 recommended for the user's level + learning track
  const recommended = useMemo(() => {
    const orderedIds = getRecommendedThemesForUser(userLevel, learningTrack);
    return orderedIds
      .map(id => pathsWithPct.find(p => p.id === id))
      .filter(Boolean)
      .slice(0, 3);
  }, [pathsWithPct, userLevel, learningTrack]);

  // All themes reordered by level relevance (for unfiltered "All Themes" section)
  const sortedPaths = useMemo(() => {
    const orderedIds = getRecommendedThemes(userLevel);
    return orderedIds
      .map(id => pathsWithPct.find(p => p.id === id))
      .filter(Boolean);
  }, [pathsWithPct, userLevel]);

  const filtered = useMemo(() => {
    const base = isFiltered ? pathsWithPct : sortedPaths;
    if (!isFiltered) return base;
    if (filterType === 'Theme')      return base.filter(p => THEME_ID_MAP[filterValue] === p.id);
    if (filterType === 'Era')        return base.filter(p => (THEME_ERAS[p.id] ?? []).includes(filterValue));
    if (filterType === 'Difficulty') return base.filter(p => p.level === filterValue);
    return base;
  }, [pathsWithPct, sortedPaths, filterType, filterValue, isFiltered]);

  function goTheme(path) {
    try { navigate(`/theme/${path.id}`); }
    catch { setNavError(true); }
  }

  function goSurprise() {
    try { navigate(`/theme/${pickSurpriseTheme(userLevel)}`); }
    catch { setNavError(true); }
  }

  return (
    <div className="phone-shell">
      <div className="accent-bar" />

      <div className="screen-content">

        {/* ── Header ── */}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'var(--em)', opacity: 0.6, marginBottom: '0.2rem',
          }}>مسارات التعلم</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ fontSize: '1.5rem', color: 'var(--text)', fontStyle: 'italic' }}>All Paths</div>
            <button
              className="question-btn"
              onClick={() => setSpotlightOpen(true)}
              aria-label="Help"
            >?</button>
          </div>
          <div style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'var(--text3)', marginTop: '0.2rem',
          }}>
            {pathsWithPct.length} classical themes · Your level: {userLevel}
          </div>
        </div>

        <div style={{ padding: '1.25rem 1.5rem 2rem', position: 'relative', zIndex: 1 }}>

          {/* ── Poet search + filter bar ── */}
          <div ref={filterAreaRef}>
            <PoetSearch
              poets={poets}
              selected={selectedPoets}
              onChange={setSelectedPoets}
            />
            {!poetFiltered && (
              <FilterBar
                types={FILTER_TYPES}
                options={FILTER_OPTIONS}
                filterType={filterType}   setFilterType={setFilterType}
                filterValue={filterValue} setFilterValue={setFilterValue}
              />
            )}
          </div>

          {/* ── Error fallback ── */}
          {navError && (
            <div style={{
              fontSize: '0.75rem', color: 'rgba(239,68,68,0.8)',
              marginBottom: '0.75rem', padding: '0.6rem 0.85rem',
              border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4,
            }}>
              Couldn't open this path — try again.
            </div>
          )}

          {/* ── Poet poem results ── */}
          {poetFiltered && (
            <div>
              <div className="home-section-title">
                <span>{selectedPoets.map(p => p.nameEn || p.nameAr).join(', ')}</span>
                {!poetPoemsLoading && (
                  <span style={{
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.58rem', letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'var(--text3)',
                  }}>
                    {poetPoemsTotal} poem{poetPoemsTotal !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {poetPoemsLoading ? (
                <ArabicLoader message="Gathering poems…" />
              ) : poetPoems.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: '0.85rem', padding: '2rem 0', fontStyle: 'italic' }}>
                  No poems found for this poet.
                </div>
              ) : (
                poetPoems.map(poem => (
                  <PoemCard
                    key={poem.id}
                    poem={poem}
                    onClick={() => {
                      if (!poem.id) return;
                      navigate(`/poem/${poem.id}`);
                    }}
                  />
                ))
              )}
            </div>
          )}

          {/* ── Pinned themes (always at top when not in poet filter) ── */}
          {!poetFiltered && pinned.length > 0 && (
            <div style={{ marginBottom: '1.75rem' }}>
              <div className="home-section-title">
                <span>★ Pinned</span>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                  tap ★ to unpin
                </span>
              </div>
              {pathsWithPct
                .filter(p => pinned.includes(p.id))
                .map(p => (
                  <PathCard key={p.id} path={p} onPress={() => goTheme(p)} pinned onPin={togglePin} />
                ))
              }
            </div>
          )}

          {/* ── Recommended for You (hidden when filtered or poet view active) ── */}
          {!isFiltered && !poetFiltered && (
            <div ref={cardsRef} style={{ marginBottom: '1.75rem' }}>
              <div className="home-section-title">
                <span>Recommended for You</span>
                <span className="tag tag-em">{userLevel}</span>
              </div>
              {recommended.map(p => (
                <PathCard key={p.id} path={p} onPress={() => goTheme(p)} pinned={pinned.includes(p.id)} onPin={togglePin} />
              ))}
            </div>
          )}

          {/* ── Surprise me — always pinned at bottom, hidden only during poet filter ── */}
          {!poetFiltered && (
            <div ref={discoverRef} style={{ marginTop: 0, marginBottom: '1.75rem' }}>
              <div className="home-section-title"><span>Discover</span></div>

              <div
                onClick={goSurprise}
                style={{ background: 'linear-gradient(135deg, rgba(229,150,83,0.07), rgba(140,207,225,0.06))', border: '1px solid rgba(229,150,83,0.24)', borderRadius: 6, padding: '1.1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer', transition: 'all 0.15s', marginBottom: '0.6rem' }}
              >
                <div className="path-icon" style={{ background: 'rgba(229,150,83,0.09)', border: '1px solid rgba(229,150,83,0.22)', fontSize: '1.15rem' }}>✨</div>
                <div style={{ flex: 1 }}>
                  <div className="path-name">Surprise me</div>
                  <div className="path-meta">Opens a theme pitched to your level — or beyond</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontStyle: 'italic', marginTop: '0.25rem', lineHeight: 1.5 }}>
                    Let the dīwān choose your next journey.
                  </div>
                </div>
                <span style={{ color: 'var(--gold)', fontSize: '1.1rem', flexShrink: 0 }}>→</span>
              </div>
            </div>
          )}

          {/* ── All / filtered paths (hidden when poet filter active) ── */}
          {!poetFiltered && (
            <div>
              <div className="home-section-title">
                <span>{isFiltered ? `${filterType}: ${filterValue}` : 'All Themes'}</span>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                  {filtered.length} path{filtered.length !== 1 ? 's' : ''}
                </span>
              </div>

              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: '0.85rem', marginTop: '2rem', fontStyle: 'italic', lineHeight: 1.75 }}>
                  No paths match — try a different filter.
                  <br />
                  <button
                    onClick={() => setFilterValue('All')}
                    style={{ marginTop: '0.75rem', background: 'none', border: '1px solid var(--border)', borderRadius: 3, padding: '0.45rem 1rem', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer' }}
                  >
                    Clear filter
                  </button>
                </div>
              ) : (
                filtered.map(p => (
                  <PathCard key={p.id} path={p} onPress={() => goTheme(p)} pinned={pinned.includes(p.id)} onPin={togglePin} />
                ))
              )}
            </div>
          )}


          <div style={{ padding: '1.5rem 1.5rem 0.5rem' }}>
            <BugReportLink screen="AllPaths" />
          </div>

        </div>
      </div>

      <BottomNav />
      {spotlightOpen && (
        <SpotlightTour
          steps={[
            { ref: filterAreaRef, title: 'Filter & Search',  desc: 'Search by poet name, or filter by theme, era, and difficulty to find exactly what you want to read.' },
            { ref: cardsRef,      title: 'Reading Paths',    desc: 'Each card is a classical theme. Recommended paths are matched to your level — tap any to explore its poems.' },
            { ref: discoverRef,   title: 'Surprise me',      desc: 'Tap this to be taken to a random theme pitched at or above your level. Good for when you want the dīwān to choose for you.' },
          ]}
          onDone={() => setSpotlightOpen(false)}
        />
      )}
    </div>
  );
}
